import React from 'react'
import { observer } from 'mobx-react'
import { personalOfferContainer } from 'src/models'
import { isPOSiteInvalid, redirectToPOLanding } from 'src/utils/mySiteUtils'
import { MetaDecorator } from 'src/views/components/MetaDecorator'
import NoOfferHere from './NoOfferHere'

/**
 * Renders the "No Offer Here" view for invalid personal offer (PO) sites.
 *
 * Encapsulates the duplicated pattern used across Cart, Checkout, ViewCart,
 * CheckoutView2, and ExpressCheckout pages:
 *   1. call `isPOSiteInvalid()`
 *   2. when invalid, render `<NoOfferHere />` (optionally wrapped with
 *      `<MetaDecorator />` when a `metaTitle` is supplied).
 *
 * Returns `null` when the PO site is valid so callers can use it as an
 * early-return guard in their `render()`:
 *
 *   const poView = <POInvalidNoOffer metaTitle={title} location={this.props} />
 *   // or via the helper:
 *   const poView = renderPOInvalidNoOffer({ metaTitle: title, location: this.props })
 *   if (poView) return poView
 */
const POInvalidNoOfferComponent = ({
  metaTitle = '',
  metaDescription = '',
  location = {},
}) => {
  const poInvalid = isPOSiteInvalid()
  if (!poInvalid?.invalid) return null

  return (
    <>
      {metaTitle ? (
        <MetaDecorator
          title={metaTitle}
          imageUrl=""
          imageAlt=""
          description={metaDescription || metaTitle}
          location={location}
        />
      ) : null}
      <NoOfferHere
        onButtonClick={redirectToPOLanding}
        userId={personalOfferContainer?.poSiteSponsor || ''}
        pitchId={personalOfferContainer?.savedPitchID || ''}
        reason={poInvalid.reason}
      />
    </>
  )
}

const POInvalidNoOffer = observer(POInvalidNoOfferComponent)

/**
 * Functional helper that returns the rendered element (or `null` if the PO
 * site is valid). Useful for class component `render()` early-return:
 *
 *   const poView = renderPOInvalidNoOffer({ metaTitle, location: this.props })
 *   if (poView) return poView
 */
const renderPOInvalidNoOffer = props => {
  if (!isPOSiteInvalid()?.invalid) return null
  return <POInvalidNoOffer {...props} />
}

export default POInvalidNoOffer
export { POInvalidNoOffer, renderPOInvalidNoOffer }
